import React, { useCallback, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  zoomPlugin,
  Filler
);

function getLatestTime(data) {
  if (!data || data.length === 0) {
    return new Date(); // Return current date as fallback
  }

  const timestamps = data.map((d) => new Date(d.time).getTime());
  const maxTimestamp = Math.max(...timestamps);
  return new Date(maxTimestamp);
}

// Pure function to get date one year before a reference date
function getOneYearAgo(referenceDate) {
  if (!(referenceDate instanceof Date) || isNaN(referenceDate.getTime())) {
    throw new Error("Invalid reference date");
  }

  const oneYearAgo = new Date(referenceDate);
  oneYearAgo.setFullYear(referenceDate.getFullYear() - 1);
  return oneYearAgo;
}

const AssessmentScoreGraph = React.memo(
  ({ patientsAssessmentScoreHistory, label, maxValue = 10 }) => {
    const chartRef = useRef(null);

    const data = {
      datasets: [
        {
          label: label,
          data: patientsAssessmentScoreHistory?.map((item) => ({
            x: new Date(item.time),
            y: item.value,
          })),
          fill: true, // fill the area under the line
          borderColor: "#F6781B",
          backgroundColor: "rgba(246, 120, 27, 0.4)", // semi-transparent orange fill
          tension: 0.3,
          pointRadius: 5,
          borderWidth: 3,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: "time",
          min:
            patientsAssessmentScoreHistory &&
            getOneYearAgo(getLatestTime(patientsAssessmentScoreHistory)),
          max:
            patientsAssessmentScoreHistory &&
            getLatestTime(patientsAssessmentScoreHistory),
          minRange: 30 * 24 * 60 * 60 * 1000, // Minimum 30 days range
          time: {
            unit: "month",
            tooltipFormat: "dd MMM yyyy",
            displayFormats: {
              month: "MMM",
            },
          },
        },
        y: {
          min: 0,
          max: maxValue,

          ticks: {
            stepSize: 1,
          },
        },
      },
      plugins: {
        zoom: {
          pan: {
            enabled: true,
            mode: "x",
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: "x",
          },
        },
      },
    };
    const handleChartPan = useCallback(() => {
      if (!chartRef.current || !patientsAssessmentScoreHistory?.length) return;

      const chart = chartRef.current;

      const resetZoom = () => {
        if (chart.resetZoom) chart.resetZoom();
      };

      const panChart = () => {
        chart.pan({ x: -200 }, undefined, "x", { duration: 300 });
      };

      const zoomTimer = setTimeout(resetZoom, 100);
      const panTimer = setTimeout(panChart, 150);

      return () => {
        clearTimeout(zoomTimer);
        clearTimeout(panTimer);
      };
    }, [patientsAssessmentScoreHistory]); // Only recreate when history changes

    useEffect(() => {
      const cleanup = handleChartPan();
      return cleanup; // Cleanup timeouts on unmount or re-run
    }, [handleChartPan]);

    return (
      <>
        <div>
          <div style={{ height: 450, width: "100%", marginTop: "38px" }}>
            <Line ref={chartRef} data={data} options={options} />
          </div>
        </div>
      </>
    );
  }
);

export default AssessmentScoreGraph;
